import React from "react";
import { Link } from "react-router-dom";
import { ListTodo, AlertTriangle, CheckCircle2 } from "lucide-react";

interface PendingTask {
  _id: string;
  title: string;
  description: string;
  priority: string;
  reward: string | null;
  type: string;
}

interface PendingTasksCardProps {
  tasks: PendingTask[];
  loading?: boolean;
}

const PendingTasksCard: React.FC<PendingTasksCardProps> = ({
  tasks,
  loading = false,
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "high":
      case "urgent":
        return "text-red-500";
      case "medium":
        return "text-yellow-500";
      default:
        return "text-gray-400";
    }
  };

  const getTaskLink = (task: PendingTask) => {
    if (task.type === "verification") return "/profile";
    if (task.type === "project_start") return "/projects";
    return "#";
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-28"></div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <ListTodo className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">Pending Tasks</h3>
        </div>
        <div className="text-center py-6 text-gray-500">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />
          <p className="text-sm">All caught up!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <ListTodo className="w-5 h-5 text-orange-600" />
        <h3 className="font-semibold text-gray-900">Pending Tasks</h3>
        <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </div>

      <div className="space-y-2">
        {tasks.slice(0, 4).map((task) => (
          <Link
            key={task._id}
            to={getTaskLink(task)}
            className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg hover:border-orange-200 hover:bg-orange-50/50 transition-colors"
          >
            <AlertTriangle
              className={`w-4 h-4 mt-0.5 ${getPriorityColor(task.priority)}`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{task.title}</p>
              <p className="text-xs text-gray-500 truncate">
                {task.description}
              </p>
            </div>
            {/* {task.reward && (
              <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                <Gift className="w-3 h-3" />
                {task.reward}
              </div>
            )} */}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default PendingTasksCard;
